import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, User, ArrowRight } from "lucide-react";
import type { Employee } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { coverageTypeLabel } from "@/lib/coverageLabel";
import type { Enrollment } from "@shared/schema";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { toYmdLocal } from "@/lib/utils";
import {
  resolveCellElection,
  cellElectionTooltip,
  type CellElection,
  type ElectionState,
} from "@/lib/employeeSummaryElections";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface EmployeeSummaryProps {
  employees: Employee[];
  plansData: any[];
  clientId?: string;
  prospectId?: string;
}

const EmployeeSummary = ({ employees, plansData, clientId, prospectId }: EmployeeSummaryProps) => {
  const entityId = clientId || prospectId;
  const { data: enrollments = [], isLoading } = useQuery<Enrollment[]>({
    queryKey: ["/api/enrollments", entityId],
    queryFn: async () => {
      const queryParam = clientId ? `clientId=${clientId}` : `prospectId=${prospectId}`;
      const res = await fetch(`/api/enrollments?${queryParam}`);
      if (!res.ok) throw new Error("Failed to fetch enrollments");
      return res.json();
    },
    enabled: !!entityId,
  });

  const sortedEmployees = useMemo(() => {
    return sortEmployeesByFullName(employees);
  }, [employees]);

  const planIdToType = new Map<number, string>();
  (plansData || []).forEach((p: any) => {
    if (p && typeof p.id === "number")
      planIdToType.set(
        p.id,
        String(p.type || "")
          .toLowerCase()
          .trim()
      );
  });

  // Determine which plan types are available for this client (controls which columns to show)
  const availableTypesSet = new Set<string>();
  (plansData || []).forEach((p: any) => {
    const t = String(p?.type || "")
      .toLowerCase()
      .trim();
    if (t) availableTypesSet.add(t);
  });
  const preferredOrder = [
    "health",
    "dental",
    "vision",
    "life",
    "disability",
    "fsa",
    "hsa",
    "voluntary",
  ];
  const otherTypes = Array.from(availableTypesSet)
    .filter((t) => !preferredOrder.includes(t))
    .sort();
  const visibleTypes = [
    ...preferredOrder.filter((t) => availableTypesSet.has(t)),
    ...otherTypes,
  ];

  // The summary is a live snapshot: rows effective after today show as a
  // "future" marker, not as current enrolled/waived (DEV-361).
  const todayYmd = toYmdLocal(new Date())!;
  const employeesWithTypes = sortedEmployees.map((e: any) => {
    const rowsByType = new Map<string, any[]>();
    (enrollments as any[])
      .filter((enr) => enr.employeeId === e.id && enr.dependentId == null)
      .forEach((enr) => {
        const t = planIdToType.get(enr.planId as number);
        if (!t) return;
        rowsByType.set(t, [...(rowsByType.get(t) ?? []), enr]);
      });
    const states = new Map<string, CellElection>();
    rowsByType.forEach((rows, t) => states.set(t, resolveCellElection(rows, todayYmd)));
    return { employee: e, states };
  });

  const totals = Object.fromEntries(
    visibleTypes.map((t) => [t, { enrolled: 0, waived: 0, futureEnrolled: 0, futureWaived: 0 }])
  ) as Record<string, { enrolled: number; waived: number; futureEnrolled: number; futureWaived: number }>;

  employeesWithTypes.forEach(({ states }) => {
    visibleTypes.forEach((k) => {
      const cell = states.get(k);
      if (cell?.current === "enrolled") totals[k].enrolled += 1;
      else if (cell?.current === "waived") totals[k].waived += 1;
      // Future elections counted only where nothing is in effect today, so an
      // employee is never counted twice.
      else if (cell?.future?.kind === "enrolled") totals[k].futureEnrolled += 1;
      else if (cell?.future?.kind === "waived") totals[k].futureWaived += 1;
    });
  });

  const label = coverageTypeLabel;

  const typeColorStyles: Record<
    string,
    { box: string; number: string; text: string }
  > = {
    health: {
      box: "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800",
      number: "text-green-600",
      text: "text-green-700 dark:text-green-300",
    },
    dental: {
      box: "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800",
      number: "text-blue-600",
      text: "text-blue-700 dark:text-blue-300",
    },
    vision: {
      box: "bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800",
      number: "text-purple-600",
      text: "text-purple-700 dark:text-purple-300",
    },
    life: {
      box: "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800",
      number: "text-orange-600",
      text: "text-orange-700 dark:text-orange-300",
    },
    disability: {
      box: "bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800",
      number: "text-yellow-600",
      text: "text-yellow-700 dark:text-yellow-300",
    },
    fsa: {
      box: "bg-teal-50 dark:bg-teal-950/20 border-teal-200 dark:border-teal-800",
      number: "text-teal-600",
      text: "text-teal-700 dark:text-teal-300",
    },
    hsa: {
      box: "bg-indigo-50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800",
      number: "text-indigo-600",
      text: "text-indigo-700 dark:text-indigo-300",
    },
  };

  const defaultTypeStyle = {
    box: "bg-muted/30 border-muted",
    number: "text-foreground",
    text: "text-muted-foreground",
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Employee Benefits Summary
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Overview of all employee benefit elections across coverage types
        </p>
        <div className="flex flex-wrap gap-4 mt-2 text-xs text-muted-foreground">
          <span><span className="text-green-600 font-bold">✓</span> Enrolled</span>
          <span><span className="text-amber-600 font-bold">W</span> Waived</span>
          <span><span className="text-red-600 font-bold">✗</span> No election</span>
          <span className="inline-flex items-center gap-1">
            <span className="w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-950/20 inline-flex items-center justify-center">
              <ArrowRight className="h-3 w-3 text-blue-600 dark:text-blue-400" />
            </span>
            Future
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading…</div>
        ) : sortedEmployees.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No employees found</p>
          </div>
        ) : (
          <>
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium sticky left-0 bg-muted/50 z-10 min-w-[200px]">
                        Employee
                      </th>
                      {visibleTypes.map((t) => (
                        <th
                          key={t}
                          className="text-center px-4 py-3 text-sm font-medium min-w-[100px]"
                        >
                          {label(t)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {employeesWithTypes.map(({ employee, states }) => {
                      const markStyles: Record<ElectionState, { box: string; text: string; glyph: string }> = {
                        enrolled: { box: "bg-green-100 dark:bg-green-950/20", text: "text-green-600 dark:text-green-400", glyph: "✓" },
                        waived: { box: "bg-amber-100 dark:bg-amber-950/20", text: "text-amber-600 dark:text-amber-400", glyph: "W" },
                        none: { box: "bg-red-100 dark:bg-red-950/20", text: "text-red-600 dark:text-red-400", glyph: "✗" },
                      };
                      const renderMark = (cell: CellElection) => {
                        const showFuture = cell.current === "none" && !!cell.future;
                        const m = markStyles[cell.current];
                        return (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center justify-center">
                                  {showFuture ? (
                                    <div
                                      className="w-5 h-5 rounded-full flex items-center justify-center bg-blue-100 dark:bg-blue-950/20"
                                      data-testid="employee-summary-future-mark"
                                    >
                                      <ArrowRight className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                    </div>
                                  ) : (
                                    <div className={`relative w-5 h-5 rounded-full flex items-center justify-center ${m.box}`}>
                                      <span className={`${m.text} text-xs font-bold`}>{m.glyph}</span>
                                      {cell.future && (
                                        // Current election in effect, with another one coming.
                                        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-500 ring-1 ring-background" />
                                      )}
                                    </div>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>{cellElectionTooltip(cell)}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      };

                      return (
                        <tr key={employee.id} className="hover:bg-muted/30">
                          <td className="px-4 py-3 sticky left-0 bg-background z-10 border-r">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                                <User className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {employee.firstName} {employee.lastName}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {employee.department || "Unassigned"}
                                </p>
                              </div>
                            </div>
                          </td>
                          {visibleTypes.map((t) => (
                            <td key={t} className="px-4 py-3 text-center">
                              {renderMark(states.get(t) ?? { current: "none", future: null })}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {visibleTypes.map((t) => {
                const styles = typeColorStyles[t] || defaultTypeStyle;
                return (
                  <div
                    key={t}
                    className={`text-center p-3 rounded-lg border ${styles.box}`}
                  >
                    <div className={`text-lg font-bold ${styles.number}`}>
                      {totals[t].enrolled}
                    </div>
                    <div className={`text-xs ${styles.text}`}>{label(t)} enrolled</div>
                    {totals[t].waived > 0 && (
                      <div className="text-xs text-amber-600 dark:text-amber-400">
                        {totals[t].waived} waived
                      </div>
                    )}
                    {totals[t].futureEnrolled > 0 && (
                      <div className="text-xs text-blue-600 dark:text-blue-400">
                        {totals[t].futureEnrolled} future
                      </div>
                    )}
                    {totals[t].futureWaived > 0 && (
                      <div className="text-xs text-blue-600 dark:text-blue-400">
                        {totals[t].futureWaived} future waiver{totals[t].futureWaived === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeSummary;
